export interface HelperReadinessItem {
  label: string
  done: boolean
}

export interface HelperMeetingItem {
  id: string
  title: string
  workspaceId: string
  startsAt: number | null
  endsAt: number | null
  status: string
  sourceProvider: string
  joinUrl: string | null
  host: string | null
  attendeeIds: string[]
  agendaNodes: string[]
  readinessChecklist: HelperReadinessItem[]
}

export type HelperMeetingRecord = {
  id: string
  data: Record<string, unknown>
}

const millis = (value: unknown): number | null => {
  if (!value) return null
  if (typeof value === 'object' && 'toMillis' in (value as Record<string, unknown>)) {
    try {
      return (value as { toMillis: () => number }).toMillis()
    } catch {
      return null
    }
  }
  if (typeof value === 'string') {
    const parsed = Date.parse(value)
    return Number.isNaN(parsed) ? null : parsed
  }
  if (typeof value === 'number') return value
  return null
}

const normalizeReadiness = (value: unknown): HelperReadinessItem[] => {
  if (!Array.isArray(value)) return []
  return value
    .map((entry) => {
      if (!entry || typeof entry !== 'object') return null
      const data = entry as Record<string, unknown>
      return {
        label: typeof data.label === 'string' ? data.label : '',
        done: data.done === true,
      }
    })
    .filter((entry): entry is HelperReadinessItem => entry !== null && entry.label.length > 0)
}

const normalizeAgenda = (value: unknown): string[] => {
  if (!Array.isArray(value)) return []
  return value
    .map((entry) => {
      if (typeof entry === 'string') return entry
      if (entry && typeof entry === 'object') {
        const data = entry as Record<string, unknown>
        if (typeof data.nodeId === 'string') return data.nodeId
        if (typeof data.id === 'string') return data.id
      }
      return null
    })
    .filter((entry): entry is string => Boolean(entry))
}

const normalizeAttendees = (value: unknown): string[] => {
  if (!Array.isArray(value)) return []
  return value.flatMap((entry) => {
    if (typeof entry === 'string' && entry.length > 0) return [entry]
    if (!entry || typeof entry !== 'object') return []
    const data = entry as Record<string, unknown>
    if (data.external === true) return []
    const userId = typeof data.userId === 'string' && data.userId.trim().length > 0
      ? data.userId
      : typeof data.uid === 'string' && data.uid.trim().length > 0
        ? data.uid
        : typeof data.id === 'string' && data.id.trim().length > 0
          ? data.id
          : null
    return userId ? [userId] : []
  })
}

const normalizeHelperMeeting = (id: string, data: Record<string, unknown>): HelperMeetingItem => {
  const startsAt = millis(data.scheduledAt) ?? millis(data.startsAt)
  const duration = typeof data.durationMinutes === 'number' ? data.durationMinutes : 30
  const participantIds = normalizeAttendees(data.participants)
  return {
    id,
    title: typeof data.title === 'string' ? data.title : 'Untitled meeting',
    workspaceId: typeof data.workspaceId === 'string' ? data.workspaceId : '',
    startsAt,
    endsAt: millis(data.endsAt) ?? millis(data.scheduledEndAt) ?? (startsAt ? startsAt + duration * 60_000 : null),
    status: typeof data.status === 'string' ? data.status : 'SCHEDULED',
    sourceProvider: typeof data.sourceProvider === 'string' ? data.sourceProvider : 'GOOGLE_MEET',
    joinUrl: typeof data.joinUrl === 'string' ? data.joinUrl : null,
    host: typeof data.hostName === 'string' ? data.hostName : (typeof data.organizerName === 'string' ? data.organizerName : null),
    attendeeIds: participantIds.length > 0 ? participantIds : normalizeAttendees(data.attendeeIds),
    agendaNodes: normalizeAgenda(data.agendaNodes),
    readinessChecklist: normalizeReadiness(data.readinessChecklist),
  }
}

export const projectHelperMeetings = (
  records: HelperMeetingRecord[],
  uid: string,
): HelperMeetingItem[] => records
  .map((entry) => normalizeHelperMeeting(entry.id, entry.data))
  .filter((item) => item.attendeeIds.includes(uid))
  .sort((left, right) => (left.startsAt ?? Number.MAX_SAFE_INTEGER) - (right.startsAt ?? Number.MAX_SAFE_INTEGER))
